import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, CardMedia,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Box, Chip, Button, Accordion, AccordionSummary, AccordionDetails,
  SelectChangeEvent, Checkbox, ListItemText, Skeleton, Alert,
  Badge, Fade, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { BandRecommendation } from '../types/BandRecommendations';
import axiosInstance from '../utils/axiosConfig';
import ScrollToTopButton from '../components/ScrollToTopButton';
import PublicAudioPlayer from '../components/public/PublicAudioPlayer';
import IrishJazzLogo from '../components/public/IrishJazzLogo';
import PromoterInterestModal from '../components/public/PromoterInterestModal';

// ─── Utilities ───────────────────────────────────────────────────────────────

function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  }) as T;
}

const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const bandSizeOptions = [
  { label: 'Soloist', min: 1, max: 1 },
  { label: 'Duo', min: 2, max: 2 },
  { label: 'Trio', min: 3, max: 3 },
  { label: 'Quartet', min: 4, max: 4 },
  { label: 'Quintet', min: 5, max: 5 },
  { label: 'Sextet', min: 6, max: 6 },
  { label: 'Septet', min: 7, max: 7 },
  { label: 'Octet', min: 8, max: 8 },
  { label: 'Nonet', min: 9, max: 9 },
  { label: 'Big Band', min: 10, max: Infinity },
];

function getSizeLabel(total: number): string {
  return bandSizeOptions.find(o => total >= o.min && total <= o.max)?.label ?? `${total} members`;
}

function getDailyFeaturedBand(bands: BandRecommendation[]): BandRecommendation | null {
  if (!bands.length) return null;
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86_400_000
  );
  const seed = now.getFullYear() * 1000 + dayOfYear;
  const hash = Math.abs(((seed * 1_664_525 + 1_013_904_223) | 0));
  return bands[hash % bands.length];
}

// ─── Filters ─────────────────────────────────────────────────────────────────

interface Filters {
  genre: string[];
  bandSize: string;
  instrument: string;
  searchTerm: string;
}

const defaultFilters: Filters = {
  genre: [],
  bandSize: '',
  instrument: '',
  searchTerm: '',
};

// ─── 1. Featured Hero ─────────────────────────────────────────────────────────

const FeaturedHero: React.FC<{ band: BandRecommendation; onGenreClick?: (g: string) => void }> = ({ band, onGenreClick }) => {
  const description = stripHtml(band.about || '');
  const total = band.maleMembers + band.femaleMembers;

  // Pick the best press quote: featured first, then any with a quote
  const pressQuote = band.externalValidation?.find(v => v.featured && v.quote)
    ?? band.externalValidation?.find(v => v.quote);

  return (
    <Box
      component={Link}
      to={`/band/${band._id}?view=public`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        mb: 4,
        cursor: 'pointer',
        '&:hover .hero-image': { transform: 'scale(1.04)' },
        '&:hover .hero-play': { opacity: 1 },
      }}
    >
      <Box
        className="hero-image"
        sx={{
          height: { xs: 340, md: 480 },
          backgroundImage: `url(${band.mainImage || '/placeholder-image.jpg'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.6s ease',
        }}
      />

      {/* Gradient */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.1) 100%)',
      }} />

      {/* Play icon */}
      <Box className="hero-play" sx={{
        position: 'absolute', top: '40%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 72, height: 72, borderRadius: '50%',
        bgcolor: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0, transition: 'opacity 0.3s ease',
      }}>
        <PlayArrowIcon sx={{ color: 'white', fontSize: 36 }} />
      </Box>

      {/* Content */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 4 } }}>
        <Chip label="Artist of the Day" size="small" sx={{
          mb: 1.5, bgcolor: 'primary.main', color: 'white',
          fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }} />

        <Typography variant="h3" component="h2" color="white" fontWeight={700}
          sx={{ mb: 1, textShadow: '0 2px 8px rgba(0,0,0,0.5)', lineHeight: 1.1 }}>
          {band.artistName}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: description ? 1.5 : 0 }}>
          {band.genres.map(g => (
            <Tooltip key={g} title={`Filter by ${g}`} placement="top">
              <Chip label={g} size="small"
                onClick={onGenreClick ? (e) => { e.preventDefault(); e.stopPropagation(); onGenreClick(g); } : undefined}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)', color: 'white', backdropFilter: 'blur(4px)',
                  cursor: onGenreClick ? 'pointer' : 'default',
                  '&:hover': onGenreClick ? { bgcolor: 'rgba(255,255,255,0.3)' } : {},
                }}
              />
            </Tooltip>
          ))}
          <Chip label={getSizeLabel(total)} size="small" sx={{
            bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.75)',
          }} />
        </Box>

        {description && (
          <Typography variant="body1" color="rgba(255,255,255,0.8)"
            sx={{ maxWidth: 600, display: { xs: 'none', sm: 'block' }, mb: pressQuote ? 2 : 0 }}>
            {description.substring(0, 200)}{description.length > 200 && '…'}
          </Typography>
        )}

        {/* 2. Press quote on hero */}
        {pressQuote?.quote && (
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'flex-start', gap: 1, mt: 2,
            p: 2, borderRadius: 1,
            bgcolor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)',
            maxWidth: 560,
          }}>
            <FormatQuoteIcon sx={{ color: 'primary.light', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" color="rgba(255,255,255,0.9)" fontStyle="italic" lineHeight={1.5}>
                {pressQuote.quote.substring(0, 140)}{pressQuote.quote.length > 140 && '…'}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.55)" sx={{ mt: 0.5, display: 'block' }}>
                — {pressQuote.source}{pressQuote.author ? `, ${pressQuote.author}` : ''}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── 1. Genre Strip ───────────────────────────────────────────────────────────

interface GenreStripProps {
  bands: BandRecommendation[];
  activeGenres: string[];
  onToggle: (genre: string) => void;
}

const GenreStrip: React.FC<GenreStripProps> = ({ bands, activeGenres, onToggle }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Derive genres sorted by frequency
  const genresByFrequency = useMemo(() => {
    const freq: Record<string, number> = {};
    bands.forEach(b => b.genres.forEach(g => { freq[g] = (freq[g] || 0) + 1; }));
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([genre, count]) => ({ genre, count }));
  }, [bands]);

  if (!genresByFrequency.length) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', letterSpacing: '0.1em' }}>
        Browse by Genre
      </Typography>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 1,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {genresByFrequency.map(({ genre, count }) => {
          const active = activeGenres.includes(genre);
          return (
            <Chip
              key={genre}
              label={`${genre} (${count})`}
              onClick={() => onToggle(genre)}
              variant={active ? 'filled' : 'outlined'}
              color={active ? 'primary' : 'default'}
              sx={{
                flexShrink: 0,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

// ─── 4. Recently Added Row ────────────────────────────────────────────────────

const RecentlyAddedRow: React.FC<{ bands: BandRecommendation[] }> = ({ bands }) => {
  const recent = useMemo(() =>
    [...bands]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 8),
    [bands]
  );

  if (!recent.length) return null;

  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Recently Added</Typography>
        <Button
          component={Link}
          to="/bands"
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          View all
        </Button>
      </Box>
      <Box sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        pb: 1,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}>
        {recent.map(band => (
          <Box
            key={band._id}
            component={Link}
            to={`/band/${band._id}?view=public`}
            sx={{
              textDecoration: 'none',
              flexShrink: 0,
              width: 200,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)' },
              '&:hover .recent-overlay': { opacity: 1 },
            }}
          >
            <Box sx={{
              height: 140,
              backgroundImage: `url(${band.mainImage || '/placeholder-image.jpg'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 2,
            }} />
            <Box className="recent-overlay" sx={{
              position: 'absolute', inset: 0, borderRadius: 2,
              background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 60%)',
              opacity: 0.6, transition: 'opacity 0.3s ease',
            }} />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 1.5 }}>
              <Typography variant="body2" fontWeight={700} color="white" noWrap>
                {band.artistName}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.7)" noWrap>
                {band.genres[0] || ''}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── 6. About IMC Strip ───────────────────────────────────────────────────────

const AboutIMCStrip: React.FC<{ onPromoterClick: () => void }> = ({ onPromoterClick }) => (
  <Box sx={{ mt: 8, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
    {/* About IMC */}
    <Box sx={{
      p: { xs: 3, md: 5 }, borderRadius: 3,
      bgcolor: 'primary.main', color: 'white',
      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
      alignItems: { md: 'center' }, gap: 3,
    }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: '0.1em' }}>About</Typography>
        <Typography variant="h5" fontWeight={700} gutterBottom>Improvised Music Company</Typography>
        <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 600, lineHeight: 1.7 }}>
          IMC is Ireland's leading organisation for jazz and improvised music — developing artists,
          building audiences, and connecting Irish music with the world since 1992.
          ShowRunnr is our platform for discovering the next generation of Irish artists.
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          href="https://www.improvisedmusic.ie"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          Visit IMC
        </Button>
      </Box>
    </Box>

    {/* Promoter hook */}
    <Box sx={{
      p: { xs: 3, md: 5 }, borderRadius: 3,
      background: 'linear-gradient(135deg, #0d1b3e 0%, #0a2a1a 100%)',
      color: 'white',
      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
      alignItems: { md: 'center' }, gap: 3,
    }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="overline" sx={{ color: '#50E3C2', letterSpacing: '0.1em' }}>
          Are you a promoter or programmer?
        </Typography>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mt: 0.5 }}>
          We can do the hard work for you
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 620, lineHeight: 1.75 }}>
          Don't see exactly what you need? IMC represents artists who aren't yet on this platform,
          and our network goes far deeper than this list. Tell us your brief — venue size, genre,
          budget bracket, dates — and we'll come back with options tailored to your programming.
          No spam, no obligation. Just good music.
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Button
          variant="contained"
          size="large"
          onClick={onPromoterClick}
          sx={{
            bgcolor: '#50E3C2', color: '#0a0a0f', fontWeight: 700,
            '&:hover': { bgcolor: '#7EECD4', transform: 'scale(1.03)' },
            transition: 'all 0.2s ease',
            px: 4, py: 1.5,
          }}
        >
          Get in touch with IMC
        </Button>
      </Box>
    </Box>
  </Box>
);

// ─── 7. Dead End ──────────────────────────────────────────────────────────────

const EndOfListBlock: React.FC<{ total: number; onShuffle: () => void }> = ({ total, onShuffle }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h5" fontWeight={700} gutterBottom>
      You've seen all {total} artists
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      Feeling lucky? Let us pick one for you.
    </Typography>
    <Button variant="contained" size="large" startIcon={<ShuffleIcon />} onClick={onShuffle}>
      Random Artist
    </Button>
  </Box>
);

// ─── 5. Editorial Pull Quote ─────────────────────────────────────────────────

interface EditorialQuoteProps {
  band: BandRecommendation;
  quote: string;
  source: string;
  author?: string;
  flip?: boolean;
}

const EditorialPullQuote: React.FC<EditorialQuoteProps> = ({ band, quote, source, author, flip = false }) => {
  const imageColor = band.mainImage ? 'var(--editorial-tint, rgba(25,118,210,0.08))' : 'transparent';
  return (
  <Box
    component={Link}
    to={`/band/${band._id}?view=public`}
    className="editorial-block"
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: flip ? 'row-reverse' : 'row' },
      textDecoration: 'none',
      borderTop: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'divider',
      my: 1,
      overflow: 'hidden',
      position: 'relative',
      transition: 'border-color 0.4s ease',
      // Border lines animate outward from centre
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        left: '50%',
        right: '50%',
        height: '2px',
        bgcolor: 'primary.main',
        transition: 'left 0.45s ease, right 0.45s ease',
        zIndex: 1,
      },
      '&::before': { top: 0 },
      '&::after': { bottom: 0 },
      '&:hover::before, &:hover::after': { left: 0, right: 0 },
      '&:hover': {
        borderColor: 'transparent',
        '& .editorial-cta': { color: 'primary.main', letterSpacing: '0.2em' },
        '& .editorial-image': {
          filter: 'saturate(1.15) brightness(1.05)',
        },
        '& .editorial-text': {
          transform: 'translateY(-4px)',
          backgroundImage: flip
            ? `linear-gradient(to left, ${imageColor}, transparent 60%)`
            : `linear-gradient(to right, ${imageColor}, transparent 60%)`,
        },
      },
    }}
  >
    {/* Image — expands on hover, Ken Burns on the inner img */}
    {band.mainImage && (
      <Box
        className="editorial-image"
        sx={{
          flexShrink: 0,
          width: { xs: '100%', md: '30%' },
          aspectRatio: { xs: '16/9', md: '1 / 1' },
          overflow: 'hidden',
          transition: 'filter 0.5s ease',
          position: 'relative',
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 10s ease',
            transform: 'scale(1.04)',
          },
          '.editorial-block:hover & img': {
            transform: 'scale(1.22) translate(2%, -1%)',
            transition: 'transform 0.65s ease',
          },
        }}
      >
        <img src={band.mainImage} alt={band.artistName} />
      </Box>
    )}

    {/* Quote content */}
    <Box
      className="editorial-text"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: { xs: 4, md: 5 },
        px: { xs: 3, md: 5 },
        textAlign: { xs: 'center', md: flip ? 'right' : 'left' },
        transition: 'transform 0.4s ease, background-image 0.5s ease',
        backgroundImage: 'none',
      }}
    >
      <FormatQuoteIcon
        sx={{
          fontSize: 32,
          color: 'primary.main',
          opacity: 0.35,
          mb: 1.5,
          alignSelf: { xs: 'center', md: flip ? 'flex-end' : 'flex-start' },
          transform: flip ? 'scaleX(-1)' : 'none',
        }}
      />

      <Typography
        variant="h5"
        component="blockquote"
        fontStyle="italic"
        fontWeight={400}
        sx={{ lineHeight: 1.5, mb: 2, color: 'text.primary' }}
      >
        "{quote}"
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        — {source}{author ? `, ${author}` : ''}
      </Typography>

      <Typography
        className="editorial-cta"
        variant="overline"
        sx={{
          letterSpacing: '0.15em',
          color: 'text.disabled',
          transition: 'all 0.25s ease',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: flip ? 'flex-end' : 'flex-start' },
          gap: 0.75,
        }}
      >
        {band.artistName}
        <ArrowForwardIosIcon sx={{ fontSize: 9 }} />
      </Typography>
    </Box>
  </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PublicBandListPage: React.FC = () => {
  const navigate = useNavigate();
  const [bands, setBands] = useState<BandRecommendation[]>([]);
  const [quoteSeed] = useState(() => Math.floor(Math.random() * 100));
  const [filtered, setFiltered] = useState<BandRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sortOrder, setSortOrder] = useState<'random' | 'az'>('random');
  const [featuredBand, setFeaturedBand] = useState<BandRecommendation | null>(null);

  useEffect(() => {
    const fetchBands = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get<BandRecommendation[]>('/api/bandrecommendations');
        const visible = data.filter(b => b.status !== 'rejected');
        // Pick featured band from stable pre-shuffle order so it stays constant all day
        setFeaturedBand(getDailyFeaturedBand(visible));
        // Fisher-Yates shuffle — genuinely random on every page load
        const shuffled = [...visible];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        setBands(shuffled);
        setFiltered(shuffled);
        setError(null);
      } catch {
        setError('Failed to load artists. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchBands();
  }, []);

  const applyFilters = useCallback(
    debounce(() => {
      const result = bands.filter(band => {
        const total = band.maleMembers + band.femaleMembers;
        return (
          (filters.genre.length === 0 || filters.genre.some(g => band.genres.includes(g))) &&
          (filters.bandSize === '' || (() => {
            const opt = bandSizeOptions.find(o => o.label === filters.bandSize);
            return opt ? total >= opt.min && total <= opt.max : true;
          })()) &&
          (filters.instrument === '' || (band.instrumentation || []).some(i =>
            i.instrumentName.toLowerCase().includes(filters.instrument.toLowerCase()))) &&
          (filters.searchTerm === '' ||
            band.artistName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            stripHtml(band.about || '').toLowerCase().includes(filters.searchTerm.toLowerCase()))
        );
      });
      const sorted = sortOrder === 'az'
        ? [...result].sort((a, b) => a.artistName.trim().localeCompare(b.artistName.trim()))
        : result;
      setFiltered(sorted);
    }, 300),
    [bands, filters, sortOrder]
  );

  useEffect(() => { applyFilters(); }, [applyFilters]);

  const [promoterModal, setPromoterModal] = useState<{ open: boolean; bandName?: string }>({ open: false });
  const [queue, setQueue] = useState<Set<string>>(new Set());
  const [playNowId, setPlayNowId] = useState<string | null>(null);
  const [activeBandId, setActiveBandId] = useState<string | null>(null);
  const [playerIsPlaying, setPlayerIsPlaying] = useState(false);

  const toggleQueue = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setQueue(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const playNow = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Use a new object ref each click so useEffect always fires, even for same id
    setPlayNowId(id + '_' + Date.now());
  };

  const handlePlayerStateChange = (bandId: string | null, isPlaying: boolean) => {
    setActiveBandId(bandId);
    setPlayerIsPlaying(isPlaying);
  };

  const playerBands = useMemo(() => {
    if (queue.size === 0) return filtered;
    return filtered.filter(b => b._id && queue.has(b._id));
  }, [filtered, queue]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.genre.length > 0) n++;
    if (filters.bandSize) n++;
    if (filters.instrument) n++;
    if (filters.searchTerm) n++;
    return n;
  }, [filters]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>
  ) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const toggleGenre = (genre: string) => {
    setFilters(prev => ({
      ...prev,
      genre: prev.genre.includes(genre)
        ? prev.genre.filter(g => g !== genre)
        : [...prev.genre, genre],
    }));
  };

  const clearFilters = () => setFilters(defaultFilters);

  const goToRandomBand = () => {
    const pool = filtered.length ? filtered : bands;
    if (!pool.length) return;
    navigate(`/band/${pool[Math.floor(Math.random() * pool.length)]._id}?view=public`);
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3, mb: 4 }} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, mb: 4 }} />
        <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2, mb: 4 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <Skeleton variant="rectangular" height={180} />
                <CardContent>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg">

      {/* Page editorial header */}
      <Box sx={{ pt: { xs: 5, md: 7 }, pb: { xs: 3, md: 4 } }}>

        {/* Supertitle — centred */}
        <Typography
          variant="overline"
          sx={{
            color: 'text.disabled',
            letterSpacing: '0.2em',
            fontSize: '0.7rem',
            display: 'block',
            textAlign: 'center',
            mb: { xs: 3, md: 4 },
          }}
        >
          Improvised Music Company presents
        </Typography>

        {/* Two-column lockup */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: { xs: 3, md: 0 },
        }}>

          {/* LEFT — logo lockup */}
          <IrishJazzLogo />

          {/* RIGHT — blurb, fills remaining width with a dividing rule */}
          <Box sx={{
            flex: 1,
            borderLeft: { md: '1px solid rgba(255,255,255,0.08)' },
            pl: { md: 6 },
            display: 'flex',
            alignItems: 'center',
          }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                lineHeight: 1.8,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
              }}
            >
              A discovery portal for jazz and improvised music on the island of Ireland.
              From traditional roots to the experimental edge — explore the artists
              defining Ireland's most vital musical conversation.
            </Typography>
          </Box>

        </Box>
      </Box>

      {/* 1. Artist of the Day hero + press quote */}
      {featuredBand && <FeaturedHero band={featuredBand} onGenreClick={toggleGenre} />}

      {/* 1. Genre strip */}
      <GenreStrip bands={bands} activeGenres={filters.genre} onToggle={toggleGenre} />

      {/* 4. Recently Added */}
      <RecentlyAddedRow bands={bands} />

      {/* Audio player + queue indicator */}
      {queue.size > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1, mb: 1, borderRadius: 2,
          bgcolor: 'rgba(80,227,194,0.1)', border: '1px solid rgba(80,227,194,0.3)',
        }}>
          <Typography variant="body2" sx={{ color: '#50E3C2', fontWeight: 600 }}>
            🎵 Playing {queue.size} selected artist{queue.size !== 1 ? 's' : ''}
          </Typography>
          <Button size="small" onClick={() => setQueue(new Set())}
            sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Clear queue
          </Button>
        </Box>
      )}
      <PublicAudioPlayer
        bands={playerBands.length ? playerBands : bands}
        playNowId={playNowId}
        onStateChange={handlePlayerStateChange}
      />

      {/* Standalone search bar */}
      <TextField
        fullWidth
        name="searchTerm"
        placeholder="Search artists by name, genre or description…"
        value={filters.searchTerm}
        onChange={handleChange}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <Box component="span" sx={{ mr: 1, color: 'text.disabled', display: 'flex' }}>
              🔍
            </Box>
          ),
        }}
      />

      {/* Grid header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" color="text.secondary">
          {filtered.length} of {bands.length} artists
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant={sortOrder === 'random' ? 'contained' : 'outlined'}
            startIcon={<ShuffleIcon />}
            onClick={() => setSortOrder('random')}
          >
            Discover
          </Button>
          <Button
            size="small"
            variant={sortOrder === 'az' ? 'contained' : 'outlined'}
            onClick={() => setSortOrder('az')}
          >
            A – Z
          </Button>
          <Button variant="outlined" size="small" startIcon={<ShuffleIcon />}
            onClick={goToRandomBand} disabled={!filtered.length}>
            Random Artist
          </Button>
          {activeFilterCount > 0 && (
            <Button size="small" onClick={clearFilters}>
              Clear filters ({activeFilterCount})
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Accordion
        sx={{
          mb: 3,
          bgcolor: 'primary.main',
          color: 'white',
          borderLeft: '4px solid',
          borderColor: 'secondary.main',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', gap: 1.5, pr: 1 } }}
        >
          <Badge badgeContent={activeFilterCount} color="secondary">
            <Typography fontWeight={600} sx={{ color: 'white' }}>Filter Artists</Typography>
          </Badge>
          {activeFilterCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} onClick={e => e.stopPropagation()}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', fontStyle: 'italic' }}>
                {[...filters.genre, filters.bandSize, filters.instrument].filter(Boolean).join(' · ')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', px: 1.5, py: 0.25, minWidth: 0, fontSize: '0.75rem', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                Clear
              </Button>
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Genre</InputLabel>
                <Select name="genre" multiple value={filters.genre} onChange={handleChange}
                  label="Genre" renderValue={(s) => (s as string[]).join(', ')}>
                  {bands.flatMap(b => b.genres)
                    .filter((g, i, arr) => arr.indexOf(g) === i)
                    .sort()
                    .map(g => (
                      <MenuItem key={g} value={g}>
                        <Checkbox checked={filters.genre.includes(g)} />
                        <ListItemText primary={g} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Band Size</InputLabel>
                <Select name="bandSize" value={filters.bandSize} onChange={handleChange} label="Band Size">
                  <MenuItem value="">All Sizes</MenuItem>
                  {bandSizeOptions.map(o => (
                    <MenuItem key={o.label} value={o.label}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth name="instrument" label="Instrument"
                value={filters.instrument} onChange={handleChange}
                helperText="e.g. piano, tenor sax, flute" />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" size="small" onClick={clearFilters}>Clear All</Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Grid */}
      <Grid container spacing={3}>
        {filtered.length > 0 ? (<>{ (() => {
          // Pre-compute bands that have a usable press quote
          const quotableBands = filtered.filter(b =>
            b.externalValidation?.some(v => v.quote && v.source)
          );

          return filtered.map((band, index) => {
            const total = band.maleMembers + band.femaleMembers;
            const description = stripHtml(band.about || '');

            // 3. Social proof — up to 2 press sources
            const pressSources = (band.externalValidation || [])
              .filter(v => v.source)
              .slice(0, 2);

            // Normal card (always standard size now)
            const card = (
              <Grid item xs={12} sm={6} md={4} key={band._id}>
                <Fade in timeout={300} style={{ transitionDelay: `${Math.min(index, 8) * 40}ms` }}>
                  <Link to={`/band/${band._id}?view=public`}
                    style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
                    <Card sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-6px) scale(1.01)',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                        '& .card-media': { transform: 'scale(1.08)' },
                        '& .card-overlay': { opacity: 1 },
                      },
                    }}>
                      <Box sx={{ position: 'relative', overflow: 'hidden', height: 180, flexShrink: 0 }}>
                        <CardMedia component="img" className="card-media"
                          sx={{
                            height: '100%', width: '100%', objectFit: 'cover',
                            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                          image={band.mainImage || '/placeholder-image.jpg'}
                          alt={band.artistName}
                        />
                        {(() => {
                          const hasVideo = band.youtubeEmbeds?.length > 0;
                          const isThisPlaying = activeBandId === band._id && playerIsPlaying;
                          return (
                            <>
                              <Box className="card-overlay" sx={{
                                position: 'absolute', inset: 0,
                                background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.6))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: isThisPlaying ? 1 : 0, transition: 'opacity 0.3s ease',
                              }}>
                                {hasVideo ? (
                                  <Tooltip title={isThisPlaying ? 'Pause' : 'Play now'}>
                                    <Box onClick={(e) => playNow(band._id!, e)} sx={{
                                      width: 52, height: 52, borderRadius: '50%', bgcolor: '#50E3C2',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      cursor: 'pointer', transition: 'transform 0.2s ease',
                                      '&:hover': { transform: 'scale(1.1)' },
                                    }}>
                                      {isThisPlaying
                                        ? <PauseIcon sx={{ color: '#0a0a0f', fontSize: 28 }} />
                                        : <PlayArrowIcon sx={{ color: '#0a0a0f', fontSize: 28 }} />
                                      }
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="No video available">
                                    <Box sx={{
                                      px: 1.5, py: 0.75, borderRadius: 2,
                                      bgcolor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                                    }}>
                                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                                        No video
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>

                              {/* Queue toggle — top right, only for bands with video */}
                              {hasVideo ? (
                                <Tooltip title={queue.has(band._id!) ? 'Remove from queue' : 'Add to queue'}>
                                  <Box onClick={(e) => toggleQueue(band._id!, e)} sx={{
                                    position: 'absolute', top: 8, right: 8,
                                    width: 30, height: 30, borderRadius: '50%',
                                    bgcolor: queue.has(band._id!) ? '#50E3C2' : 'rgba(0,0,0,0.55)',
                                    border: '1.5px solid',
                                    borderColor: queue.has(band._id!) ? '#50E3C2' : 'rgba(255,255,255,0.4)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', backdropFilter: 'blur(4px)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { transform: 'scale(1.15)', bgcolor: queue.has(band._id!) ? '#7EECD4' : 'rgba(0,0,0,0.75)' },
                                    zIndex: 2,
                                  }}>
                                    <Typography sx={{
                                      fontSize: 16, lineHeight: 1, fontWeight: 700,
                                      color: queue.has(band._id!) ? '#0a0a0f' : 'white',
                                    }}>
                                      {queue.has(band._id!) ? '✓' : '+'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Tooltip title="No video available">
                                  <Box sx={{
                                    position: 'absolute', top: 8, right: 8,
                                    width: 30, height: 30, borderRadius: '50%',
                                    bgcolor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    zIndex: 2,
                                  }}>
                                    <Typography sx={{ fontSize: 14, lineHeight: 1 }}>🎵</Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </>
                          );
                        })()}
                      </Box>

                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h5" gutterBottom color="text.primary"
                          sx={{ transition: 'all 0.3s ease', '.MuiCard-root:hover &': { color: 'primary.main' } }}>
                          {band.artistName}
                        </Typography>
                        <Box mb={1}>
                          {band.genres.map(genre => (
                            <Tooltip key={genre} title={`Filter by ${genre}`} placement="top">
                              <Chip
                                label={genre}
                                size="small"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGenre(genre); }}
                                sx={{
                                  mr: 0.5, mb: 0.5, cursor: 'pointer',
                                  bgcolor: filters.genre.includes(genre) ? 'primary.main' : undefined,
                                  color: filters.genre.includes(genre) ? 'white' : undefined,
                                  '&:hover': { bgcolor: 'primary.light', color: 'white' },
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          {getSizeLabel(total)}
                        </Typography>
                        {description && (
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            {description.substring(0, 120)}{description.length > 120 && '…'}
                          </Typography>
                        )}
                        {/* 3. Social proof badges */}
                        {pressSources.length > 0 && (
                          <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {pressSources.map(v => (
                              <Chip key={v.source} label={v.source} size="small" variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20, borderColor: 'primary.light', color: 'primary.main' }} />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                </Fade>
              </Grid>
            );

            // 5. After every 9th card, inject a full-width editorial pull quote
            const shouldInjectQuote = (index + 1) % 9 === 0 && quotableBands.length > 0;
            if (shouldInjectQuote) {
              const quoteSlot = Math.floor(index / 9);
            const quoteBand = quotableBands[(quoteSlot + quoteSeed) % quotableBands.length];
              const quoteEntry = quoteBand.externalValidation?.find(v => v.quote && v.source);
              return (
                <React.Fragment key={`group-${index}`}>
                  {card}
                  {quoteEntry && (
                    <Grid item xs={12}>
                      <EditorialPullQuote
                        band={quoteBand}
                        quote={quoteEntry.quote!}
                        source={quoteEntry.source}
                        author={quoteEntry.author}
                        flip={quoteSlot % 2 !== 0}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              );
            }

            return card;
          });
        })() }</>) : (
          <Grid item xs={12}>
            <Box textAlign="center" py={6}>
              {activeFilterCount > 0 ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No artists found matching your filters
                  </Typography>
                  <Button onClick={clearFilters} variant="outlined">Clear All Filters</Button>
                </>
              ) : (
                <Typography variant="h6" color="text.secondary">
                  Good music is on the way…
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      {/* 7. Dead end */}
      {filtered.length > 0 && <EndOfListBlock total={filtered.length} onShuffle={goToRandomBand} />}

      {/* 6. About IMC */}
      <AboutIMCStrip onPromoterClick={() => setPromoterModal({ open: true })} />
      <PromoterInterestModal
        open={promoterModal.open}
        bandName={promoterModal.bandName}
        onClose={() => setPromoterModal({ open: false })}
      />

      <ScrollToTopButton backgroundColor="#1976d2" color="#ffffff" scrollThreshold={500} size={50} />
    </Container>
  );
};

export default PublicBandListPage;
